import {useAtom} from 'jotai';
import styled from 'styled-components';
import {Typography} from '@/components/Typography';
import {AddressSearchHistoryItem, AddressSearchItem, SubwaySearchItem} from '@/map/@types';
import {mapAddressSearchHistoryAtom} from '@/map/atom';
import {AddressKey} from '@/map/enums';
import {getSubwayLineColor} from '@/map/utils';
import {Flexbox, FlexFullGrow, IconClose, Section} from '@/styled-components';
import {palette} from '@/styles';
import {AddrBadge} from '../StyledComponents';

interface IProps {
  onClick(item?: AddressSearchHistoryItem): void;
}

const getSearchTime = (time: number) => {
  const t = new Date(time);
  return `${t.getMonth() + 1}.${t.getDate()}`;
};

export const SearchHistoryList = ({onClick}: IProps) => {
  const [history, setHistory] = useAtom(mapAddressSearchHistoryAtom);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const dataIdx = e.currentTarget.dataset?.index;
    const index = Number(dataIdx);

    if (index >= 0) {
      onClick?.(history[index]);
    }
  };

  const handleRemove = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const dataIdx = e.currentTarget.dataset?.index;
    const index = Number(dataIdx);

    if (index >= 0) {
      setHistory((prev) => {
        prev = [...prev];
        prev.splice(index, 1);
        return prev;
      });
    }
  };

  return (
    <>
      <Typography variant="medium12" color={palette.gray600} as="div">
        최근 검색어
      </Typography>
      <Section spacing={1}>
        {!history?.length ? (
          <Typography
            variant="medium14"
            color={palette.gray400}
            as="div"
            style={{height: 130}}
          >
            최근 검색어 내역이 없습니다.
          </Typography>
        ) : (
          history.slice(0, 10).map((i, idx) => (
            <Item
              id={`search-history-${idx}`}
              key={idx}
              data-index={idx}
              onClick={handleClick}
            >
              <Flexbox alignItems="baseline">
                <ItemContent item={i} />
                <FlexFullGrow />
                <Flexbox>
                  {!!i.createdTime && (
                    <Typography
                      variant="medium12"
                      color={palette.gray400}
                      style={{marginLeft: 8}}
                    >
                      {getSearchTime(i.createdTime)}
                    </Typography>
                  )}
                  <IconRemove data-index={idx} onClick={handleRemove} />
                </Flexbox>
              </Flexbox>
            </Item>
          ))
        )}
      </Section>
    </>
  );
};

const ItemContent = ({item}: {item: AddressSearchHistoryItem}) => {
  if (!item) {
    return null;
  }

  if (item.keyword) {
    return (
      <Typography variant="medium14" as="div" ellipsis>
        {item.keyword}
      </Typography>
    );
  }

  if (!item.origin) {
    return null;
  }

  if (item.mainKey) {
    return (
      <div>
        <Typography variant="medium14" as="div">
          {(item.origin as AddressSearchItem)[item.mainKey]}
        </Typography>
        {!!item.subKey && (
          <Section spacing={1}>
            <Flexbox>
              <AddrBadge>
                {item.subKey === AddressKey.addressName ? '도로명' : '지번'}
              </AddrBadge>
              <Typography
                variant="medium12"
                as="div"
                color={palette.textSecondary}
              >
                {(item.origin as AddressSearchItem)[item.subKey]}
              </Typography>
            </Flexbox>
          </Section>
        )}
      </div>
    );
  }

  return (
    <Typography variant="medium14" as="div" ellipsis>
      {'line' in item.origin && (
        <>
          <Typography color={getSubwayLineColor(item.origin)}>
            {item.origin.line}
          </Typography>
          <Typography color={palette.gray400}>{' | '}</Typography>
        </>
      )}
      {(item.origin as SubwaySearchItem).name}
    </Typography>
  );
};

const IconRemove = styled(IconClose)`
  margin-left: 4px;
  flex-shrink: 0;
`;

const Item = styled.div`
  padding: 7px 4px;
  border-radius: 5px;
  cursor: pointer;
  :hover {
    background-color: ${palette.gray100};
  }
`;
